import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, insuranceOperationsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedInsuranceQuoteRecord, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-insurance-quotes',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, InputTextModule, AinAmountPipe],
  template: `
    <ain-page [title]="t('sales.regulated.insurance.title')" [description]="t('sales.regulated.insurance.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.regulated.insurance.policiesNav')" severity="secondary" (click)="goPolicies()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.insurance.fields.product') }}</th>
                <th>{{ t('sales.regulated.insurance.fields.premium') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.productCode }}</button></td>
                <td>{{ row.premiumAmount | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesInsuranceQuotesComponent implements OnInit, OnDestroy {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveTopic: string | null = null;
  private liveEventsSubscription: Subscription | null = null;
  readonly loading = signal(true);
  readonly rows = signal<RegulatedInsuranceQuoteRecord[]>([]);
  readonly selected = signal<RegulatedInsuranceQuoteRecord[]>([]);
  ngOnInit(): void {
    this.load();
    void this.startLiveUpdates();
  }
  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    if (this.liveTopic) {
      void this.liveUpdateService.unsubscribe(this.liveTopic);
      this.liveTopic = null;
    }
  }
  load(): void {
    this.loading.set(true);
    this.api.listInsuranceQuotes().subscribe({
      next: r => { this.rows.set(r); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }
  onSelect(v: RegulatedInsuranceQuoteRecord[]): void { this.selected.set(v ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/insurance-quotes/new']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/insurance-quotes', id]); }
  goPolicies(): void { void this.router.navigate(['/sales/insurance-policies']); }
  liveStatusTranslationKey(state: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'): string {
    return liveStatusTranslationKey(state);
  }
  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = insuranceOperationsTopic(tenantId);
    this.liveTopic = topic;
    await this.liveUpdateService.subscribe(topic);
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(
        filter(event =>
          event.eventType === LiveUpdateEventTypes.insuranceQuotesUpdated
          || event.eventType === LiveUpdateEventTypes.insuranceClaimsUpdated
          || event.eventType === LiveUpdateEventTypes.insurancePoliciesUpdated))
      .subscribe(() => this.load());
  }
  t(key: string): string { return this.localization.translate(key); }
}
